// Guide registry for the "Skip the forms — go ask Mara about you" second door
// (WU-W5). This is the DATA half of the navigation contract: it maps a shareable
// slug (?ask=<slug>) to a personality-NPC guide, the room that hosts them, and
// the trait lens they explore. Resolution of the runtime NPC id happens at
// runtime by matching `displayName` against the live roster's `playerName`
// (never a hardcoded guid) — see App.tsx guided-open effect.

/**
 * The 7 frozen trait-dimension keys (contract §2.3 `TRAIT_POLES`).
 * Declared locally as a narrow string-literal union to keep WU-W5 dependency-free
 * (Wave 0). Will unify with `memory.ts` `TraitDimensionKey` once the web
 * foundation lands.
 */
export type TraitDimensionKey =
  | 'social_energy'
  | 'openness'
  | 'emotional_expression'
  | 'stability_vs_adventure'
  | 'play_competitiveness'
  | 'romance_style'
  | 'relationship_pace';

export interface GuidedNpc {
  /** Shareable URL slug (?ask=<slug>) and the in-app trigger key. */
  slug: string;
  /** Optional hardcoded runtime id override — normally resolved from the roster. */
  npcId?: string;
  /** MUST match the live roster `playerName` and `TRAIT_POLES.*.npc` verbatim. */
  displayName: string;
  /** Room that hosts this guide; falls back to `GUIDE_ROOM_CODE` when absent. */
  roomCode?: string;
  /** Primary trait lens this guide explores (contract §2.3). */
  lens: TraitDimensionKey;
  /** Short, lens-flavored CTA/blurb copy. */
  blurb: string;
}

/**
 * The room code that hosts the personality NPCs (Mara / Sister Lyra / Rooftop
 * Poet / Old Tomas / Vex). Env-overridable via `VITE_GUIDE_ROOM_CODE`.
 *
 * !!! DEPLOY-TIME CONFIG !!! The fallback below is a placeholder. Before any
 * prod deploy this MUST equal the real prod social-room `code` that hosts the
 * five guides (verify with `curl -s https://sync.direct/api/rooms | jq` or ask
 * the user). Set `VITE_GUIDE_ROOM_CODE` in the web build env so the fallback is
 * never relied upon in prod.
 */
export const GUIDE_ROOM_CODE: string =
  (import.meta.env as Record<string, string | undefined>).VITE_GUIDE_ROOM_CODE ?? 'flexo-commons';

/** The 5 trait-lens guides from contract §2.3 `TRAIT_POLES` (display names verbatim). */
export const GUIDED_NPCS: GuidedNpc[] = [
  {
    slug: 'mara',
    displayName: 'Mara',
    lens: 'social_energy',
    blurb: 'how you show up in a room',
  },
  {
    slug: 'lyra',
    displayName: 'Sister Lyra',
    lens: 'openness',
    blurb: 'what you reach for — the familiar or the new',
  },
  {
    slug: 'poet',
    displayName: 'Rooftop Poet',
    lens: 'emotional_expression',
    blurb: 'how you wear your heart',
  },
  {
    slug: 'tomas',
    displayName: 'Old Tomas',
    lens: 'stability_vs_adventure',
    blurb: 'harbor keeper or horizon chaser',
  },
  {
    slug: 'vex',
    displayName: 'Vex',
    lens: 'play_competitiveness',
    blurb: 'how you play the game',
  },
];

/** The default guide when a CTA is pressed with no explicit slug (matches the copy "go ask Mara"). */
export const DEFAULT_GUIDE_SLUG = 'mara';

/**
 * Resolve a slug (from ?ask=<slug> or a CTA) to a guide. Case-insensitive.
 * Returns `undefined` for unknown/omitted slugs — callers decide whether to
 * fall back to `DEFAULT_GUIDE_SLUG` (CTAs do; a bare unknown URL slug does not).
 */
export function resolveGuide(slug: string | null | undefined): GuidedNpc | undefined {
  if (!slug) return undefined;
  const normalized = slug.toLowerCase();
  return GUIDED_NPCS.find((g) => g.slug === normalized);
}
